import * as React from "react";
import moment, { Moment } from "moment";
import Button from "@mui/material/Button";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormLabel,
  Box,
  Typography
} from "@mui/material";

import { book } from "../../../../api/bookings";
import { notificationAdd } from "../../../../actions/notification";
import CalendarDropdown from "../../../../components/CalendarDropdown/CalendarDropdown";
import { roundMinutes } from "../../../../containers/Calendar/Booking/BookingCreateEdit";
import { RootState } from "../../../../store";
import { Booking } from "../../../../interfaces/BookingInterfaces";
import { spFullNameSelector } from "../../../../reducers/serviceProvider";
import DoctorAvailabilityDialog from "./doctor-availability-dialog";
import { ServiceProvider } from "../../../../interfaces/ServiceProvidersInterface";
import useGetDepartments from "../../../../hooks/department";
import { Department } from "../../../../interfaces/DepartmentInterface";

// component
function ServiceProviderDetail({
  serviceProvider,
  departments
}: {
  serviceProvider: ServiceProvider;
  departments: Department[];
}): React.ReactNode {
  const departmentName = serviceProvider?.departmentId
    ? departments.find((dept) => String(dept.id) === String(serviceProvider.departmentId))?.name ||
      "N/A"
    : "N/A";

  const fields = [
    { label: "Name", value: spFullNameSelector(serviceProvider) },
    { label: "Department", value: departmentName },
    { label: "Qualification", value: serviceProvider?.qualification || "N/A" }
  ];

  return (
    <Box
      sx={{
        p: 1,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "5px"
      }}
    >
      {fields.map(
        (f) =>
          f.value && (
            <Box
              key={f.label}
              sx={{
                display: "flex",
                alignItems: "center",
                fontSize: "sm"
              }}
            >
              <Typography
                sx={{
                  width: "120px",
                  fontWeight: "bold"
                }}
              >
                {f.label}:
              </Typography>

              <Typography>{f.value}</Typography>
            </Box>
          )
      )}
    </Box>
  );
}

// main component
interface PropType {
  open: boolean;
  onClose: () => void;
}

export default function AppointmentDialog({ open = false, onClose }: PropType): React.ReactNode {
  const dispatch = useDispatch();
  const departments = useGetDepartments();

  const clientId = useSelector((state: RootState) => state.clientPortalUserInfo.clientId);
  const selectedSp = useSelector((state: RootState) => state.clientPortalUserInfo.selectedSp);
  const resourceCentreId = useSelector(
    (state: RootState) => state.clientPortalUserInfo.resourceCentreId
  );

  const initialStart = new Date();
  const initialEnd = new Date(initialStart.getTime() + 15 * 60 * 1000);

  const [weekDay, setWeekDay] = React.useState<number>(moment().weekday());
  const [openAvailabilityDialog, setOpenAvailabilityDialog] = React.useState(false);
  const [appointmentData, setAppointmentData] = React.useState<Partial<Booking>>({
    start: initialStart,
    end: initialEnd
  });

  const handleBookingSave = React.useCallback(async () => {
    try {
      const data = {
        ...appointmentData,
        clientId,
        serviceProviderId: selectedSp?.id || null,
        services: [],
        resourceCentreId
      };

      await book(data);

      onClose();
      dispatch(
        notificationAdd({
          id: new Date().getMilliseconds(),
          variant: "success",
          message: "Booking created successfully!",
          autoTimeout: true
        })
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      dispatch(
        notificationAdd({
          id: new Date().getMilliseconds(),
          variant: "error",
          message: error?.message,
          autoTimeout: true
        })
      );
    }
  }, [appointmentData, clientId, dispatch, onClose, resourceCentreId, selectedSp?.id]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDialog-paper": {
          minWidth: 400
        }
      }}
    >
      <DialogTitle>Book Appointment</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <DoctorAvailabilityDialog
          open={openAvailabilityDialog}
          onClose={() => {
            setOpenAvailabilityDialog(false);
          }}
          onSlotClick={(time: string) => {
            setAppointmentData({
              start: new Date(time),
              end: new Date(new Date(time).getTime() + 15 * 60 * 1000)
            });
          }}
          weekDay={weekDay}
        />

        {/* appointment service provider */}
        {selectedSp && (
          <ServiceProviderDetail serviceProvider={selectedSp} departments={departments || []} />
        )}

        {/* Appointment date */}
        <FormControl fullWidth>
          <FormLabel sx={{ mb: 0.5 }}>Appointment Date</FormLabel>
          <CalendarDropdown
            disable={false}
            fullwidth
            TextFieldProps={{ variant: "outlined" }}
            isBookingWindow
            TextFieldInputProps={{
              disableUnderline: true,
              style: { fontSize: "12px" }
            }}
            minValidDate={moment()}
            date={new Date(appointmentData.start ?? Date.now())}
            onChange={(newDate) => {
              const [startHour, startMinute] = moment(appointmentData.start)
                .format("HH mm")
                .split(" ")
                .map(Number);
              let startDate: Moment | Date = moment(newDate).set("hour", startHour);
              startDate = startDate.set("minute", startMinute);

              const [endHour, endMinute] = moment(appointmentData.end)
                .format("HH mm")
                .split(" ")
                .map(Number);
              let endDate: Moment | Date = moment(newDate).set("hour", endHour);
              endDate = endDate.set("minute", endMinute);

              setAppointmentData({
                ...appointmentData,
                start: new Date(startDate.toISOString()),
                end: new Date(endDate.toISOString())
              });

              setWeekDay(moment(newDate).weekday());
            }}
          />
        </FormControl>

        {/* doctor availability */}
        {selectedSp && (
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setOpenAvailabilityDialog(true)}
            sx={{ color: "#294166" }}
          >
            Doctor Availability
          </Button>
        )}

        {/* Start and End time on same row */}
        <Box sx={{ display: "flex", gap: 2 }}>
          <FormControl sx={{ flex: 1 }}>
            <FormLabel sx={{ mb: 0.5 }}>Start Time</FormLabel>
            <TextField
              type="time"
              size="small"
              variant="outlined"
              value={moment(appointmentData.start).format("HH:mm")}
              slotProps={{
                inputLabel: { shrink: true },
                input: {
                  disableUnderline: true,
                  style: {
                    fontSize: "12px"
                  }
                },
                htmlInput: { step: 600, sx: { fontSize: "12px" } }
              }}
              onChange={({ target }) => {
                const [hour, minute] = target.value.split(":").map(Number);
                const roundedMinute = roundMinutes(minute, 5);
                let date: Moment | Date = moment(appointmentData.start).set("hour", hour);
                date = date.set("minute", roundedMinute || 0);
                const difference = moment(appointmentData.end).diff(
                  appointmentData.start,
                  "minutes"
                );
                const endDate = new Date(moment(date).add(difference, "minutes").toISOString());
                date = new Date(moment(date).toISOString());
                setAppointmentData({ ...appointmentData, start: date, end: endDate });
              }}
            />
          </FormControl>

          <FormControl sx={{ flex: 1 }}>
            <FormLabel sx={{ mb: 0.5 }}>End Time</FormLabel>
            <TextField
              type="time"
              size="small"
              variant="outlined"
              value={moment(appointmentData.end).format("HH:mm")}
              slotProps={{
                input: {
                  disableUnderline: true,
                  style: {
                    fontSize: "12px"
                  }
                },
                htmlInput: {
                  step: 600,
                  min: `${moment(appointmentData.start).format("HH:mm")}`,
                  sx: { fontSize: "12px" }
                }
              }}
              onChange={({ target }) => {
                const [hour, minute] = target.value.split(":").map(Number);
                const roundedMinute = roundMinutes(minute, 5);

                let newEnd: Moment | Date = moment(appointmentData.end).set("hour", hour);
                newEnd = newEnd.set("minute", roundedMinute || 0);

                const startMoment = moment(appointmentData.start);

                if (moment(newEnd).isBefore(startMoment)) {
                  newEnd = startMoment.clone();
                }

                setAppointmentData({ ...appointmentData, end: new Date(newEnd.toISOString()) });
              }}
            />
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined" sx={{ color: "#294166" }}>
          Close
        </Button>

        <Button onClick={handleBookingSave} variant="contained" sx={{ backgroundColor: "#294166" }}>
          Book
        </Button>
      </DialogActions>
    </Dialog>
  );
}
